/**
 * Client helpers for background_jobs (bulk AI Tasks, etc.).
 */
import { supabase } from './supabase';

export type BackgroundJobStatus =
  | 'queued'
  | 'running'
  | 'completed'
  | 'failed'
  | 'cancelled';

export type BackgroundJobType = 'bulk_booking_followups' | string;

export interface BackgroundJob {
  id: string;
  business_id: string;
  created_by_user_id: string | null;
  type: BackgroundJobType;
  title: string;
  status: BackgroundJobStatus;
  cancel_requested: boolean;
  total_count: number;
  done_count: number;
  error_count: number;
  payload: Record<string, unknown>;
  result_summary: string | null;
  error: string | null;
  started_at: string | null;
  finished_at: string | null;
  created_at: string;
  updated_at: string;
}

export interface BulkBookingFollowupsPayload {
  booking_from: string;
  booking_to: string;
  booking_status?: string;
  /** When set, only bookings for these customers get follow-up Tasks
   * (user named specific customers instead of "all bookings"). */
  customer_ids?: string[] | null;
  due_offset_months?: number;
  due_offset_days?: number;
  due_base?: 'completed_at' | 'end_date';
  title_template?: string;
  list?: string | null;
  assignee_resource_id?: string | null;
  notes_template?: string | null;
}

function requireSupabase() {
  if (!supabase) throw new Error('Supabase client is not initialized');
  return supabase;
}

export async function listBackgroundJobs(
  businessId: string,
  opts: { limit?: number; activeOnly?: boolean } = {}
): Promise<BackgroundJob[]> {
  const client = requireSupabase();
  const limit = Math.min(Math.max(opts.limit ?? 20, 1), 50);
  let q = client
    .from('background_jobs')
    .select('*')
    .eq('business_id', businessId)
    .order('created_at', { ascending: false })
    .limit(limit);

  if (opts.activeOnly) {
    q = q.in('status', ['queued', 'running']);
  }

  const { data, error } = await q;
  if (error) throw new Error(error.message);
  return (data || []) as BackgroundJob[];
}

export async function getActiveBackgroundJobCount(businessId: string): Promise<number> {
  const client = requireSupabase();
  const { count, error } = await client
    .from('background_jobs')
    .select('id', { count: 'exact', head: true })
    .eq('business_id', businessId)
    .in('status', ['queued', 'running']);
  if (error) throw new Error(error.message);
  return count ?? 0;
}

export async function cancelBackgroundJob(
  businessId: string,
  jobId: string
): Promise<void> {
  const client = requireSupabase();
  const { error } = await client
    .from('background_jobs')
    .update({ cancel_requested: true })
    .eq('id', jobId)
    .eq('business_id', businessId)
    .in('status', ['queued', 'running']);
  if (error) throw new Error(error.message);
}

export async function kickBackgroundJobWorker(
  jobId: string,
  attempts = 3
): Promise<boolean> {
  const client = requireSupabase();
  for (let attempt = 0; attempt < attempts; attempt++) {
    const { data, error } = await client.functions.invoke('background-jobs-worker', {
      body: { job_id: jobId },
    });
    if (!error) {
      if (data && typeof data === 'object' && (data as { ok?: boolean }).ok === false) {
        console.warn('[backgroundJobs] worker kick rejected', data);
      } else {
        return true;
      }
    } else {
      console.warn('[backgroundJobs] worker kick failed', error);
    }
    if (attempt < attempts - 1) {
      await new Promise((r) => setTimeout(r, 400 * (attempt + 1)));
    }
  }
  return false;
}

/** Kick every queued job for this business (safe to call on app load). */
export async function kickQueuedJobs(jobs: BackgroundJob[]): Promise<void> {
  for (const job of jobs) {
    if (job.status === 'queued') {
      await kickBackgroundJobWorker(job.id);
    }
  }
}

/** Re-kick jobs stuck in queued (worker never picked up). Safe to call often. */
export async function kickStaleQueuedJobs(
  _businessId: string,
  jobs: BackgroundJob[],
  maxAgeMs = 15_000
): Promise<void> {
  const stale = jobs.filter((j) => {
    if (j.status !== 'queued') return false;
    const age = Date.now() - new Date(j.created_at).getTime();
    return age >= maxAgeMs;
  });
  for (const job of stale) {
    await kickBackgroundJobWorker(job.id);
  }
}

export async function startBulkBookingFollowupsJob(
  businessId: string,
  opts: {
    title: string;
    payload: BulkBookingFollowupsPayload;
    created_by_user_id?: string | null;
  }
): Promise<BackgroundJob> {
  const client = requireSupabase();
  const { data, error } = await client
    .from('background_jobs')
    .insert({
      business_id: businessId,
      created_by_user_id: opts.created_by_user_id || null,
      type: 'bulk_booking_followups',
      title: opts.title,
      status: 'queued',
      payload: opts.payload,
    })
    .select('*')
    .single();

  if (error) throw new Error(error.message);
  const job = data as BackgroundJob;
  await kickBackgroundJobWorker(job.id);
  return job;
}

export function subscribeBackgroundJobs(
  businessId: string,
  onChange: () => void
): () => void {
  const client = requireSupabase();
  let debounce: ReturnType<typeof setTimeout> | null = null;
  const schedule = () => {
    if (debounce) clearTimeout(debounce);
    debounce = setTimeout(() => onChange(), 200);
  };

  const channel = client
    .channel(`background_jobs_${businessId}`)
    .on(
      'postgres_changes',
      {
        event: '*',
        schema: 'public',
        table: 'background_jobs',
        filter: `business_id=eq.${businessId}`,
      },
      () => schedule()
    )
    .subscribe();

  return () => {
    if (debounce) clearTimeout(debounce);
    void client.removeChannel(channel);
  };
}
